// Thin fetch wrapper around the FastAPI auth service.
//
// The backend always lives at the same origin (the auth UI is served by
// FastAPI on port 4100 in prod; vite dev proxies /auth → 4100). All
// endpoints accept and return camelCase JSON.

import { storage } from './storage.js';

const headers = (extra = {}) => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  ...extra,
});

// fetch, with a failure to reach the service as an error with a code
// (AUTH-502) rather than a bare TypeError: a screen can then show the same
// form-level alert it shows for every other failure, instead of nothing.
async function send(path, method, body, token) {
  try {
    return await fetch(path, {
      method,
      headers: headers(token ? { Authorization: `Bearer ${token}` } : {}),
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (cause) {
    const err = new Error('Network request failed');
    err.status = 0;
    err.code = 'NETWORK_ERROR';
    err.cause = cause;
    throw err;
  }
}

// A new access token from the httpOnly refresh cookie that login set, saved
// for the calls that follow. null when the session cannot be renewed.
async function renewToken() {
  try {
    const res = await fetch('/auth/refresh', { method: 'POST', headers: headers() });
    const data = res.ok ? await res.json() : null;
    if (!data?.token) return null;
    storage.saveAuth({ token: data.token, user: data.user });
    return data.token;
  } catch {
    return null;
  }
}

// The service's error envelope (auth_service/common/exceptions.py):
//   { statusCode, code, message, timestamp, path, correlationId?, retryAfter? }
// ``message`` is a list of strings, or one sentence on a 429. ``detail``
// only appears on the few responses that bypass the envelope.
function envelopeMessages(data) {
  return [].concat(data?.message ?? data?.detail?.message ?? data?.detail ?? [])
    .filter((m) => typeof m === 'string' && m.trim())
    .map((m) => m.trim().replace(/\.$/, ''));
}

async function request(path, { method = 'GET', body, token } = {}) {
  let res;
  try {
    res = await send(path, method, body, token);
    // An access token lasts 15 minutes, and onboarding can take longer: Finish
    // then answered "Invalid or expired token" with no way on (LJ-031). Renew
    // once from the refresh cookie and retry; never for the refresh call itself.
    if (res.status === 401 && token && path !== '/auth/refresh') {
      const fresh = await renewToken();
      if (fresh) res = await send(path, method, body, fresh);
    }
  } catch (cause) {
    const err = new Error('Network request failed');
    err.status = 0;
    err.code = 'NETWORK_ERROR';
    err.cause = cause;
    throw err;
  }
  const text = await res.text();
  let data = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = { raw: text }; }
  }
  if (!res.ok) {
    let messages = envelopeMessages(data);
    let code = data?.detail?.code || data?.code;
    // Still refused after the renewal: say what to do, not "Invalid token".
    if (res.status === 401 && token) {
      messages = ['Your session has expired. Please sign in again'];
      code = 'SESSION_EXPIRED';
    }
    const err = new Error(messages.join('. ') || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = code;
    err.messages = messages;
    err.correlationId = data?.correlationId;
    // 429s say when to try again (RATE_LIMITED + Retry-After).
    err.retryAfter = Number(res.headers?.get?.('Retry-After')) || Number(data?.retryAfter) || undefined;
    err.payload = data;
    throw err;
  }
  return data;
}

// "in 2 minutes", "in 45 seconds", "in a moment".
function inWords(seconds) {
  if (!seconds || seconds <= 0) return 'in a moment';
  if (seconds < 60) return `in ${seconds} second${seconds === 1 ? '' : 's'}`;
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.ceil(minutes / 60);
  return `in ${hours} hour${hours === 1 ? '' : 's'}`;
}

// "body.companyName: Field required" -> "Company name: Field required".
function fieldSentence(message) {
  const m = /^([\w.[\]-]+):\s*(.+)$/.exec(message);
  if (!m) return message;
  const field = m[1].split('.').filter((p) => !/^\d+$/.test(p)).pop() || '';
  const words = field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase();
  if (!words || words === 'body') return m[2];
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}: ${m[2]}`;
}

function reference(err) {
  return err?.correlationId ? ` Quote reference ${err.correlationId} if you contact support.` : '';
}

const sentences = (err) => `${err.messages.join('. ')}.`;

// One map from the service's error code and HTTP status to a sentence a
// person can act on (AUTH-502). Screens show it in a form-level alert;
// field errors stay for client-side validation only.
export function errorMessage(err) {
  switch (err?.code) {
    case 'INVALID_CREDENTIALS':
      return 'Email or password is incorrect.';
    case 'ACCOUNT_DISABLED':
      return 'This account is disabled. Ask your workspace owner to re-enable it, or contact support.';
    case 'MEMBERSHIP_SUSPENDED':
      return 'Your access to this workspace is suspended. Ask your workspace owner to restore it.';
    case 'ACCOUNT_LOCKED':
      // The service's sentence names the wait and the way out (reset).
      return err.messages?.length ? sentences(err) : 'Too many failed sign-in attempts. Try again later, or reset your password.';
    case 'EMAIL_EXISTS':
      return 'An account already exists for this address. Sign in instead.';
    case 'INVALID_OTP':
      return 'That code is not right or has expired. Check it and try again.';
    case 'INVALID_TOKEN':
      return 'This link has expired. Request a new one.';
    case 'SESSION_EXPIRED':
      return 'Your session has expired. Please sign in again.';
    case 'NETWORK_ERROR':
      return 'We could not reach Vivid360. Check your connection and try again.';
    default:
      break;
  }
  if (err?.status === 429) return `Too many attempts. Try again ${inWords(err.retryAfter)}.`;
  if (err?.status === 422 && err.messages?.length) {
    return `${err.messages.map(fieldSentence).join('. ')}.`;
  }
  if (err?.status >= 500) {
    return `Something went wrong on our side. Try again in a moment.${reference(err)}`;
  }
  // A known 4xx with a sentence from the service: show that sentence.
  if (err?.code && err.messages?.length) return sentences(err);
  return `That did not work. Try again.${reference(err)}`;
}

export const api = {
  register: (body) => request('/auth/register', { method: 'POST', body }),
  verifyEmail: (body) => request('/auth/verify-email', { method: 'POST', body }),
  // A fresh sign-up code. The answer is the same whether or not the
  // address has an unverified account.
  resendVerification: (email) =>
    request('/auth/resend-verification', { method: 'POST', body: { email } }),
  login: (body) => request('/auth/login', { method: 'POST', body }),
  // #139: a new access token from the existing session (the refresh
  // cookie), with ``redirectTo`` computed from ``nextUrl`` as for a login.
  continueSession: (nextUrl) => request('/auth/refresh', { method: 'POST', body: { nextUrl } }),
  logout: (refreshToken) => request('/auth/logout', { method: 'POST', body: { refreshToken } }),
  completeOnboarding: (body, token) =>
    request('/auth/onboard/complete', { method: 'POST', body, token }),
  // Dynamic module catalog, sourced from Grid's module_registry.
  getOnboardingModules: () => request('/auth/onboarding/modules'),
  // Bundle/industry catalog (curated packages), sourced from Grid's
  // industry_bundle. Onboarding is bundle-driven, not module-by-module.
  getOnboardingBundles: () => request('/auth/onboarding/bundles'),
  // D3: forgot / reset password.
  requestPasswordReset: (email, lang) =>
    request('/auth/request-password-reset', { method: 'POST', body: lang ? { email, lang } : { email } }),
  resetPassword: (token, password) =>
    request('/auth/reset-password', { method: 'POST', body: { token, password } }),
  // Invitations (the emailed /invite/<token> link).
  getInvitation: (token) => request(`/auth/invitations/${encodeURIComponent(token)}`),
  acceptInvitation: (token, body) =>
    request(`/auth/invitations/${encodeURIComponent(token)}/accept`, { method: 'POST', body }),
};
